/**
 * The Phase 3 delivery operations: workflow runs, releases, deployments,
 * insights and webhooks.
 *
 * Split from `operations.ts` for length and re-exported from it, as
 * `repo.ts` and `phase2.ts` are. One function per `/v1` operation, paths
 * checked against the generated contract.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';
import type { Paged } from './repo';

type Schemas = components['schemas'];

export type WorkflowRun = Schemas['WorkflowRun'];
export type RunState = Schemas['RunState'];
export type Release = Schemas['Release'];
export type Deployment = Schemas['Deployment'];
export type DeploymentState = Schemas['DeploymentState'];
export type InsightsSummary = Schemas['InsightsSummary'];
export type InsightsDistribution = Schemas['InsightsDistribution'];
export type Webhook = Schemas['Webhook'];
export type WebhookWithSecret = Schemas['WebhookWithSecret'];
export type WebhookDelivery = Schemas['WebhookDelivery'];
export type OrganizationSettings = Schemas['OrganizationSettings'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

/* ====================================================================== *
 * Runs, releases, deployments
 * ====================================================================== */

export async function listRepositoryWorkflowRuns(
  scope: RepoScope,
  query: { state?: RunState; branch?: string; before?: string } = {},
): Promise<Paged<WorkflowRun>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/runs', {
      params: { path: repoPath(scope), query },
    }),
  );
  return {
    data: value.data,
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

export async function listReleases(scope: RepoScope): Promise<Release[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/releases', {
      params: { path: repoPath(scope) },
    }),
  );
  return value.data;
}

export async function listDeployments(scope: RepoScope): Promise<Deployment[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/deployments', {
      params: { path: repoPath(scope) },
    }),
  );
  return value.data;
}

/* ====================================================================== *
 * Insights
 * ====================================================================== */

export function getRepositoryInsights(
  scope: RepoScope,
  query: { since?: string; until?: string; environment?: string } = {},
): Promise<InsightsSummary> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/insights', {
      params: { path: repoPath(scope), query },
    }),
  );
}

export function getOrganizationInsights(
  orgId: string,
  query: { since?: string; until?: string; environment?: string } = {},
): Promise<InsightsSummary> {
  return expect(
    http.GET('/organizations/{org_id}/insights', { params: { path: { org_id: orgId }, query } }),
  );
}

/* ====================================================================== *
 * Organization settings
 * ====================================================================== */

export function getOrganizationSettings(orgId: string): Promise<OrganizationSettings> {
  return expect(
    http.GET('/organizations/{org_id}/settings', { params: { path: { org_id: orgId } } }),
  );
}

export function updateOrganizationSettings(
  orgId: string,
  body: Partial<
    Pick<
      OrganizationSettings,
      | 'base_permission'
      | 'default_repository_visibility'
      | 'default_branch_name'
      | 'allow_public_repositories'
      | 'require_mfa'
    >
  > & { if_version?: number },
): Promise<OrganizationSettings> {
  return expect(
    http.PATCH('/organizations/{org_id}/settings', { params: { path: { org_id: orgId } }, body }),
  );
}

/* ====================================================================== *
 * Webhooks
 * ====================================================================== */

export async function listWebhooks(scope: RepoScope): Promise<Webhook[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/webhooks', {
      params: { path: repoPath(scope) },
    }),
  );
  return value.items;
}

export function createWebhook(
  scope: RepoScope,
  body: { url: string; event_types: string[]; active?: boolean },
): Promise<WebhookWithSecret> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/webhooks', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function updateWebhook(
  scope: RepoScope,
  webhookId: string,
  body: { url?: string; event_types?: string[]; active?: boolean; if_version?: number },
): Promise<Webhook> {
  return expect(
    http.PATCH('/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}', {
      params: { path: { ...repoPath(scope), webhook_id: webhookId } },
      body,
    }),
  );
}

export function deleteWebhook(scope: RepoScope, webhookId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}', {
      params: { path: { ...repoPath(scope), webhook_id: webhookId } },
    }),
  );
}

export function rotateWebhookSecret(
  scope: RepoScope,
  webhookId: string,
  windowSeconds?: number,
): Promise<WebhookWithSecret> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}/rotate-secret',
      {
        params: { path: { ...repoPath(scope), webhook_id: webhookId } },
        body: windowSeconds === undefined ? {} : { window_seconds: windowSeconds },
      },
    ),
  );
}

export function pingWebhook(scope: RepoScope, webhookId: string): Promise<WebhookDelivery> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}/ping', {
      params: { path: { ...repoPath(scope), webhook_id: webhookId } },
    }),
  );
}

export async function listWebhookDeliveries(
  scope: RepoScope,
  webhookId: string,
): Promise<WebhookDelivery[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}/deliveries', {
      params: { path: { ...repoPath(scope), webhook_id: webhookId } },
    }),
  );
  return value.items;
}

export function redeliverWebhook(
  scope: RepoScope,
  webhookId: string,
  deliveryId: string,
): Promise<WebhookDelivery> {
  return expect(
    http.POST(
      '/organizations/{org_id}/repositories/{repo_id}/webhooks/{webhook_id}/deliveries/{delivery_id}/redeliver',
      {
        params: { path: { ...repoPath(scope), webhook_id: webhookId, delivery_id: deliveryId } },
      },
    ),
  );
}
